import { cva } from 'class-variance-authority';
import { clsx } from 'clsx';
import type { ButtonColors, ButtonProps, ButtonSizes, ButtonVariants } from './types';

export const buttonBaseVariants = cva(
  'flex flex-row items-center justify-center font-semibold is-platform:font-medium shrink-0 is-platform:focus-visible:outline-none',
  {
    variants: {
      color: {
        brand: 'focus-visible:ring-brand is-platform:focus-visible:!ring-brand-shadow-sm',
        neutral: 'focus-visible:ring-gray is-platform:focus-visible:!ring-gray-shadow-sm',
        danger: 'focus-visible:ring-error is-platform:focus-visible:!ring-error-shadow-sm',
        success: 'focus-visible:ring-success is-platform:focus-visible:!ring-success-shadow-sm',
      } satisfies Record<ButtonColors, string>,
      variant: {
        // TODO: use border width from component token
        // platform swaps the navy xs shadow for the warm gray-200 platform-xs per Figma
        solid: 'border shadow-xs is-platform:!shadow-none',
        outline: 'border shadow-xs is-platform:!shadow-none',
        soft: 'border-0',
        ghost: 'border-0 hover:underline',
      } satisfies Record<ButtonVariants, string>,
    },
  },
);

export const buttonSizeVariants = cva('', {
  variants: {
    variant: {
      solid: '',
      outline: '',
      soft: '',
      ghost: '',
    } satisfies Record<ButtonVariants, string>,
    size: {
      xs: 'gap-xxs is-platform:gap-xs',
      sm: 'gap-xs',
      md: 'gap-xs',
      lg: 'gap-sm',
    } satisfies Record<ButtonSizes, string>,
  },
  compoundVariants: [
    {
      size: 'xs',
      variant: ['solid', 'outline', 'soft'],
      className: 'max-h-6 min-w-[3.25rem]',
    },
    {
      size: 'sm',
      variant: ['solid', 'outline', 'soft'],
      className: 'max-h-7 min-w-14',
    },
    {
      size: 'md',
      variant: ['solid', 'outline', 'soft'],
      className: 'max-h-8 min-w-[4.5rem]',
    },
    {
      size: 'lg',
      variant: ['solid', 'outline', 'soft'],
      className: 'max-h-12 min-w-24',
    },
  ],
});

export const iconSizeVariants = cva('shrink-0', {
  variants: {
    size: {
      xs: 'size-3',
      sm: 'size-4',
      md: 'size-4',
      lg: 'size-5',
    } satisfies Record<ButtonSizes, string>,
  },
});

export const BUTTON_SIZE_PADDING: Record<ButtonSizes, { paddingX: number; paddingY: number }> = {
  xs: { paddingX: 8, paddingY: 4 },
  sm: { paddingX: 8, paddingY: 6 },
  md: { paddingX: 12, paddingY: 6 },
  lg: { paddingX: 16, paddingY: 12 },
};

export const BUTTON_ID_CLASSNAME = 'ua-button';
export const defaultButtonClasses = (
  color: ButtonProps['color'],
  variant: ButtonProps['variant'],
  size: ButtonProps['size'],
  className: string | undefined,
) =>
  clsx(
    BUTTON_ID_CLASSNAME,
    buttonBaseVariants({
      color,
      variant,
    }),
    buttonSizeVariants({
      variant,
      size,
    }),
    className,
  );

export const getButtonGroupStyles = ({
  numberOfOptions,
  index,
}: {
  numberOfOptions: number;
  index: number;
}) => ({
  ...(index !== 0 && index !== numberOfOptions - 1
    ? {
        borderRadius: '0px',
      }
    : undefined),
  ...(index === 0
    ? {
        borderStartEndRadius: '0px',
        borderEndEndRadius: '0px',
      }
    : undefined),
  ...(index === numberOfOptions - 1
    ? {
        borderStartStartRadius: '0px',
        borderEndStartRadius: '0px',
      }
    : undefined),
});
